import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  useMediaQuery,
  useTheme,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { ENDPOINT } from "../configuration/configuration";
import LogoBetweenLines from "../components/LogoBetweenLines";
import CustomButton from "../components/CustomButton";
import QrifyMark from "../components/site/QrifyMark";

const OTPVerification = () => {
  const { id, ref_id } = useParams();
  const { traceId, phoneNumber } = useSelector((state) => state.user);
  const [otp, setOtp] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    console.log("Redux phoneNumber:", phoneNumber);
  }, [phoneNumber]);

  const handleChangeNumber = () => {
    navigate(`/qrify/${id}`, { replace: true });
  };

  const handleVerifyOtp = async () => {
    if (otp.length === 4 && traceId) {
      setLoading(true);
      try {
        const response = await fetch(ENDPOINT.Validation, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            otp: otp,
            phoneNumber: phoneNumber,
            trace_id: traceId,
            ref_id: ref_id,
          }),
        });
        console.log("Submitting OTP payload:", {
          otp,
          phoneNumber,
          trace_id: traceId,
          ref_id,
        });
        const data = await response.json();
        setLoading(false);

        if (response.ok) {
          setIsValid(true);
          navigate(`/call/${id}`, { replace: true });
        } else {
          setIsValid(false);
          console.error("Error:", data);
        }
      } catch (error) {
        setLoading(false);
        setIsValid(false);
        console.error("Error verifying OTP:", error);
      }
    } else {
      setIsValid(false);
    }
  };

  const maskedPhone = phoneNumber
    ? `••••••${String(phoneNumber).slice(-4)}`
    : null;

  return (
    <Box
      sx={{
        height: "100dvh",
        maxHeight: "100dvh",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: isMobile ? "stretch" : "center",
        background: "linear-gradient(135deg, #f5f7fa, #c3cfe2)",
        overflow: "hidden",
        position: isMobile ? "fixed" : "relative",
        inset: isMobile ? 0 : "auto",
      }}
    >
      <Stack
        sx={{
          width: isMobile ? "100%" : isTablet ? 400 : 360,
          height: isMobile ? "100%" : "min(90dvh, 640px)",
          maxHeight: "100%",
          px: isMobile ? 2 : 2.5,
          py: isMobile ? 1.5 : 2.5,
          backgroundColor: "white",
          boxShadow: isMobile ? "none" : "0 8px 16px rgba(0, 0, 0, 0.1)",
          borderRadius: isMobile ? 0 : 2,
          textAlign: "center",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: isMobile ? 1 : 1.5,
        }}
      >
        <LogoBetweenLines
          logoSource={require("../assets/images/logo.png")}
          height={isMobile ? 40 : 48}
        />

        <Typography
          sx={{
            color: "#303030",
            fontWeight: 600,
            fontSize: isMobile ? 16 : 18,
          }}
        >
          SVASTH <QrifyMark />
        </Typography>

        <Box>
          <Typography
            sx={{
              fontSize: isMobile ? 20 : 22,
              fontWeight: 700,
              color: "#303030",
            }}
          >
            Validate OTP
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: 13,
              color: "#666",
              lineHeight: 1.4,
              px: 1,
            }}
          >
            {maskedPhone
              ? `Enter the 4-digit code sent to ${maskedPhone}`
              : "Enter the 4-digit code sent to your mobile number"}
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={isMobile ? 1.5 : 2}
          justifyContent="center"
          sx={{ my: 0.5 }}
        >
          {[0, 1, 2, 3].map((index) => (
            <TextField
              key={index}
              value={otp[index] || ""}
              inputRef={inputRefs[index]}
              inputProps={{
                maxLength: 1,
                inputMode: "numeric",
                pattern: "[0-9]*",
                "aria-label": `OTP digit ${index + 1}`,
              }}
              error={!isValid}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 1);
                const newOtp = otp.split("");
                newOtp[index] = value;
                setOtp(newOtp.join(""));
                setIsValid(true);

                if (value && index < 3) {
                  inputRefs[index + 1].current?.focus();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Backspace") {
                  const newOtp = otp.split("");
                  if (otp[index]) {
                    newOtp[index] = "";
                    setOtp(newOtp.join(""));
                  } else if (index > 0) {
                    inputRefs[index - 1].current?.focus();
                    newOtp[index - 1] = "";
                    setOtp(newOtp.join(""));
                  }
                }
              }}
              onPaste={(e) => {
                e.preventDefault();
                const pasted = e.clipboardData
                  .getData("text")
                  .replace(/\D/g, "")
                  .slice(0, 4);
                if (!pasted) return;
                setOtp(pasted.padEnd(4, " ").trimEnd().slice(0, 4));
                setIsValid(true);
                const focusIndex = Math.min(pasted.length, 3);
                inputRefs[focusIndex].current?.focus();
              }}
              onFocus={() => {
                inputRefs[index].current?.select();
              }}
              sx={{
                width: isMobile ? 56 : 64,
                "& .MuiOutlinedInput-root": {
                  width: isMobile ? 56 : 64,
                  height: isMobile ? 56 : 64,
                  borderRadius: 2,
                  bgcolor: "#fafbfc",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "&.Mui-focused, &:focus-visible": {
                    boxShadow: "none",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1a237e",
                    borderWidth: 2,
                  },
                },
                "& .MuiOutlinedInput-input": {
                  textAlign: "center",
                  padding: "0 !important",
                  margin: 0,
                  fontSize: isMobile ? 22 : 26,
                  fontWeight: 600,
                  height: "100%",
                  width: "100%",
                  boxSizing: "border-box",
                  caretColor: "#1a237e",
                  "&:focus, &:focus-visible": {
                    boxShadow: "none",
                    outline: "none",
                  },
                },
              }}
            />
          ))}
        </Stack>

        <Typography
          variant="caption"
          sx={{
            color: "error.main",
            minHeight: 18,
            visibility: isValid ? "hidden" : "visible",
          }}
        >
          Invalid OTP
        </Typography>

        <Box sx={{ width: "100%" }}>
          {loading ? (
            <Box
              sx={{
                height: 50,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : (
            <CustomButton
              label="Submit"
              onClick={handleVerifyOtp}
              disabled={loading}
            />
          )}
        </Box>

        <Box
          sx={{
            pb: "max(8px, env(safe-area-inset-bottom))",
          }}
        >
          <Typography sx={{ fontSize: 14, color: "#666" }}>
            Didn&apos;t get it?
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              color: "#343275",
              fontWeight: 600,
              cursor: "pointer",
              fontSize: 14,
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={handleChangeNumber}
          >
            Change Number
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default OTPVerification;
